
import { useState, useCallback, useEffect, useRef } from 'react';

// Export the CallState interface for other files to use
export interface CallState {
  callInProgress: boolean;
  callStatus: string;
  callStartTime: Date | null;
  callSid: string | null;
  isMuted: boolean;
  isInitializingDevices: boolean;
  formattedDuration: string;
  duration: number;
  activeContactId: number | null;
  activeContactName: string | null;
}

// Export the UpdateCallStateAction type for other files to use
export type UpdateCallStateAction = (updates: Partial<CallState>) => void;

export interface CallStateHook extends CallState {
  setCallInProgress: (value: boolean) => void;
  setCallStatus: (status: string) => void;
  setCallStartTime: (time: Date | null) => void;
  setCallSid: (sid: string | null) => void;
  setIsMuted: (muted: boolean) => void;
  setIsInitializingDevices: (initializing: boolean) => void;
  setActiveContactId: (contactId: number | null) => void;
  setActiveContactName: (contactName: string | null) => void;
  endCallState: () => void;
  resetCallState: () => void;
  updateCallState: UpdateCallStateAction;
}

export const useTwilioCallState = (): CallStateHook => {
  const [callInProgress, setCallInProgress] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isInitializingDevices, setIsInitializingDevices] = useState(false);
  const [formattedDuration, setFormattedDuration] = useState('00:00');
  const [duration, setDuration] = useState(0);
  const [activeContactId, setActiveContactId] = useState<number | null>(null);
  const [activeContactName, setActiveContactName] = useState<string | null>(null);
  
  // Timer ref for duration updates
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Format duration helper
  const formatDuration = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Start duration timer when call starts
  useEffect(() => {
    if (callInProgress && callStartTime && callStatus === 'open') {
      console.log('🕐 Starting duration timer for active call');
      
      const updateDuration = () => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - callStartTime.getTime()) / 1000);
        setDuration(elapsed);
        setFormattedDuration(formatDuration(elapsed));
      };

      // Update immediately
      updateDuration();
      
      // Then update every second
      durationTimerRef.current = setInterval(updateDuration, 1000);
    } else {
      // Clear timer if call is not active or not connected
      if (durationTimerRef.current) {
        console.log('🛑 Stopping duration timer');
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    }

    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    };
  }, [callInProgress, callStartTime, callStatus, formatDuration]);

  // Soft end: marks call as ended but preserves data (callSid, duration, etc.)
  const endCallState = useCallback(() => {
    console.log('🏁 Ending call state (preserving data)');
    
    // Clear timer
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    
    setCallInProgress(false);
    setCallStatus('ended');
    setIsMuted(false);
    // Keep: callSid, formattedDuration, duration, callStartTime, activeContactId, activeContactName
    console.log('✅ Call state ended (data preserved)');
  }, []);

  // Full reset: clears everything (for dismiss or new call)
  const resetCallState = useCallback(() => {
    console.log('🔄 Resetting call state (full clear)');
    
    // Clear timer first
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    
    // Reset all state
    setCallInProgress(false);
    setCallStatus('idle');
    setCallStartTime(null);
    setCallSid(null);
    setIsMuted(false);
    setFormattedDuration('00:00');
    setDuration(0);
    setActiveContactId(null);
    setActiveContactName(null);
    
    console.log('✅ Call state reset complete');
  }, []);

  const updateCallState = useCallback((updates: Partial<CallState>) => {
    if (updates.callInProgress !== undefined) setCallInProgress(updates.callInProgress);
    if (updates.callStatus !== undefined) setCallStatus(updates.callStatus);
    if (updates.callStartTime !== undefined) setCallStartTime(updates.callStartTime);
    if (updates.callSid !== undefined) setCallSid(updates.callSid);
    if (updates.isMuted !== undefined) setIsMuted(updates.isMuted);
    if (updates.duration !== undefined) setDuration(updates.duration);
    if (updates.formattedDuration !== undefined) setFormattedDuration(updates.formattedDuration);
    if (updates.activeContactId !== undefined) setActiveContactId(updates.activeContactId);
    if (updates.activeContactName !== undefined) setActiveContactName(updates.activeContactName);
  }, []);

  return {
    callInProgress,
    setCallInProgress,
    callStatus,
    setCallStatus,
    callStartTime,
    setCallStartTime,
    callSid,
    setCallSid,
    isMuted,
    setIsMuted,
    isInitializingDevices,
    setIsInitializingDevices,
    formattedDuration,
    duration,
    activeContactId,
    setActiveContactId,
    activeContactName,
    setActiveContactName,
    endCallState,
    resetCallState,
    updateCallState,
  };
};
